import React, { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SeverityBadge } from "@/components/StatusBadge";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

const FILTERS = ["All", "Open", "Acknowledged", "In Progress", "Resolved", "Closed"];

export default function Exceptions() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("All");
  const [assign, setAssign] = useState(null);
  const [assignee, setAssignee] = useState("");
  const [resolveEx, setResolveEx] = useState(null);
  const [resolution, setResolution] = useState("");
  const [closeEx, setCloseEx] = useState(null);
  const [closeForm, setCloseForm] = useState({ root_cause: "", corrective_action: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/exceptions").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const canManage = hasPerm("exception.manage");

  const act = async (id, action, body) => {
    try { await api.post(`/exceptions/${id}/${action}`, body || {}); toast.success(`Exception ${action}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doResolve = async () => {
    setBusy(true);
    try { await api.post(`/exceptions/${resolveEx.id}/resolve`, { resolution }); toast.success("Exception resolved"); setResolveEx(null); setResolution(""); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doAssign = async () => {
    setBusy(true);
    try { await api.post(`/exceptions/${assign.id}/assign`, { owner: assignee }); toast.success("Assigned"); setAssign(null); setAssignee(""); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doClose = async () => {
    setBusy(true);
    try { await api.post(`/exceptions/${closeEx.id}/close`, closeForm); toast.success("Exception closed"); setCloseEx(null); setCloseForm({ root_cause: "", corrective_action: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!rows) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const filtered = filter === "All" ? rows : rows.filter((e) => e.status === filter);
  const counts = FILTERS.reduce((a, f) => ({ ...a, [f]: f === "All" ? rows.length : rows.filter((e) => e.status === f).length }), {});

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">Exception Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Resolution workflow: acknowledge → assign → investigate → resolve → close, with root-cause capture.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`exc-filter-${f.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f ? "bg-[#12346B] text-white border-[#12346B]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {f} <span className="opacity-70">({counts[f]})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-400 text-sm">No exceptions in this view.</div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((e) => (
            <div key={e.id} data-testid={`exception-${e.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${e.severity === "High" || e.severity === "Critical" ? "text-rose-500" : "text-orange-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="mono text-sm font-semibold text-slate-900">{e.exception_no}</span>
                  <span className="text-xs text-slate-600">{e.type}</span>
                  <SeverityBadge level={e.severity} />
                  <StatusBadge status={e.status} />
                  {e.owner && <span className="text-[11px] bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">Owner: {e.owner}</span>}
                </div>
                <div className="text-sm text-slate-600 mt-1">{e.description}</div>
                {e.resolution && <div className="text-xs text-emerald-600 mt-1">Resolution: {e.resolution}</div>}
                {e.root_cause && <div className="text-xs text-slate-500 mt-1"><b>Root cause:</b> {e.root_cause} · <b>Corrective:</b> {e.corrective_action}</div>}
                <div className="text-[11px] text-slate-400 mt-1">{fmtDate(e.created_at)}</div>
              </div>
              {canManage && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  {e.status === "Open" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(e.id, "acknowledge")} data-testid={`btn-ack-${e.id}`}>Acknowledge</Button>}
                  {["Open", "Acknowledged", "In Progress"].includes(e.status) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAssign(e)} data-testid={`btn-assign-${e.id}`}>Assign</Button>}
                  {["Open", "Acknowledged"].includes(e.status) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(e.id, "start")} data-testid={`btn-start-${e.id}`}>Start</Button>}
                  {["Open", "Acknowledged", "In Progress"].includes(e.status) && <Button size="sm" className="h-7 text-xs bg-[#12346B] hover:bg-[#12346B]/90 text-white border-0" onClick={() => setResolveEx(e)} data-testid={`btn-resolve-${e.id}`}>Resolve</Button>}
                  {e.status === "Resolved" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCloseEx(e)} data-testid={`btn-close-${e.id}`}>Close</Button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!resolveEx} onOpenChange={(o) => !o && setResolveEx(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Resolve Exception</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{resolveEx?.description}</p>
          <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Describe investigation and resolution…" data-testid="resolution-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveEx(null)}>Cancel</Button>
            <Button onClick={doResolve} disabled={busy || !resolution} data-testid="btn-confirm-resolve" className="bg-[#12346B] hover:bg-[#12346B]/90">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Assign Owner</DialogTitle></DialogHeader>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Owner (email or name)</Label>
            <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="finance@pci.com" data-testid="assign-owner" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssign(null)}>Cancel</Button>
            <Button onClick={doAssign} disabled={busy || !assignee} data-testid="btn-confirm-assign" className="bg-[#12346B] hover:bg-[#12346B]/90">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeEx} onOpenChange={(o) => !o && setCloseEx(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Close Exception</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Root Cause</Label>
              <Textarea value={closeForm.root_cause} onChange={(e) => setCloseForm((s) => ({ ...s, root_cause: e.target.value }))} data-testid="close-root-cause" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Corrective Action</Label>
              <Textarea value={closeForm.corrective_action} onChange={(e) => setCloseForm((s) => ({ ...s, corrective_action: e.target.value }))} data-testid="close-corrective" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseEx(null)}>Cancel</Button>
            <Button onClick={doClose} disabled={busy || !closeForm.root_cause} data-testid="btn-confirm-close" className="bg-[#12346B] hover:bg-[#12346B]/90">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
